import clone from 'clone'
import { DateTime } from 'luxon'

import {
  type Contract,
  type Pet,
  PetGenderEnum,
  PetSizeEnum,
  PetTypeEnum,
  type Travel,
  type Traveler,
  TravelReasonEnum,
  type Vehicle,
} from '~/layers/forms/models'
import { getMaybeRefValue } from '~/utils/getMaybeRefValue'

type QuotationParams<
  InitialParams extends object | null,
  FinalParams extends object | null = null,
> = {
  initialParams: Partial<InitialParams>
  finalParams: Partial<FinalParams>
  completedInitialParams: boolean | null
  completedFinalParams: boolean | null
} | null

export type Insurable =
  | {
      branch: Extract<SiteApiSchema<'RamoCobertura'>, 'Auto'>
      data: Vehicle
      quotationParams: QuotationParams<
        {
          manufacturer: string | null | undefined
          model: string | null | undefined
          amount: number | null
          year: number | null
          use: string | null | undefined
        },
        {
          version: string | null | undefined
        }
      >
    }
  | {
      branch: Extract<SiteApiSchema<'RamoCobertura'>, 'Viajes'>
      data: Travel
      quotationParams: QuotationParams<
        {
          destination: string | null | undefined
          destinationId: string | null | undefined
          from: Date | null
          to: Date | null
          calculatedDays: number | null
        },
        {
          insuredBirthDate: Date | null
          travelers: Traveler[]
        }
      >
    }
  | {
      branch: Extract<SiteApiSchema<'RamoCobertura'>, 'Mascotas'>
      data: Pet
      quotationParams: QuotationParams<null>
    }
  | {
      branch: Exclude<SiteApiSchema<'RamoCobertura'>, 'Auto' | 'Viajes' | 'Mascotas'> | null
      data: null
      quotationParams: null
    }

export const useInsurable = ({
  product,
  contract,
}: {
  product: MaybeRef<SiteApiSchema<'RamoCobertura'> | null | undefined>
  contract: MaybeRef<Contract>
}) =>
  computed<Insurable>(() => {
    const ramo = getMaybeRefValue(product)
    const _contract = clone(getMaybeRefValue(contract))

    switch (ramo) {
      case 'Auto': {
        const initialParams = {
          manufacturer: _contract.vehicle.manufacturer?.id,
          model: _contract.vehicle.model?.id,
          amount: _contract.vehicle.amount.value,
          year: _contract.vehicle.year,
          use: _contract.vehicle.use?.id,
        }

        const finalParams = {
          version: _contract.vehicle.version?.id,
        }

        const completedInitialParams = Object.values(initialParams).every((v) => !isNil(v))
        const completedFinalParams =
          completedInitialParams && Object.values(finalParams).every((v) => !isNil(v))

        return {
          branch: 'Auto',
          data: _contract.vehicle,
          quotationParams: {
            initialParams,
            finalParams,
            completedInitialParams,
            completedFinalParams,
          },
        }
      }
      case 'Viajes': {
        const insuredBirthDate =
          (_contract.insured.customerAsInsured
            ? _contract.customer.birthDate
            : _contract.insured.birthDate) ??
          DateTime.now().minus({ years: 21 }).startOf('day').toJSDate()

        const initialParams = {
          destination: _contract.travel.destination?.name,
          destinationId: _contract.travel.destination?.id,
          from: _contract.travel.from,
          to: _contract.travel.to,
          calculatedDays: _contract.travel.calculatedDays,
        }

        //const travelers = _contract.travelers.collection
        //
        //const { customer, insured } = _contract
        //const customerAsTraveler = Traveler.load(
        //  _contract.insured.customerAsInsured ? customer : insured
        //)
        //
        //travelers.push(customerAsTraveler)

        const finalParams = {
          insuredBirthDate,
          travelers: [],
        }

        const completedInitialParams = Object.values(initialParams).every((v) => !isNil(v))
        const completedFinalParams =
          completedInitialParams &&
          !isNil(
            _contract.insured.customerAsInsured
              ? _contract.customer.birthDate
              : _contract.insured.birthDate
          )

        return {
          branch: 'Viajes',
          data: _contract.travel,
          quotationParams: {
            initialParams,
            finalParams,
            completedInitialParams,
            completedFinalParams,
          },
        }
      }
      case 'Mascotas': {
        return {
          branch: 'Mascotas',
          data: _contract.pet,
          quotationParams: null,
        }
      }
      default:
        return {
          branch: null,
          data: null,
          quotationParams: null,
        }
    }
  })

export const getInsurableDescription = (
  insurable: Insurable
): { comments: string; insurableAsArgentoModel: object | undefined } => {
  const comments: string[] = []
  let insurableAsArgentoModel: object | undefined = undefined

  const buildComment = (data: Partial<Nullable<Record<string, string>>>) =>
    Object.entries(data)
      .filter(([_, v]) => !isNil(v))
      .map(([k, v]) => `${k} ${v}`)
      .join(', ')

  switch (insurable.branch) {
    case 'Auto': {
      const vehicle: SiteApiSchema<'VehiculoModel'> = {
        marca: insurable.data.manufacturer?.name,
        marcaId: insurable.data.manufacturer?.id,
        modelo: insurable.data.model?.name,
        modeloId: insurable.data.model?.id,
        version: insurable.data.version?.name,
        versionId: insurable.data.version?.id,
        yearVehiculo: insurable.data.year ? insurable.data.year.toString() : undefined,
        valorAuto: insurable.data.amount.value,
      }

      const vehicleInfo = buildComment({
        'Es un': vehicle.marca,
        ['modelo']: vehicle.modelo,
        ['versión']: vehicle.version,
        'del año': vehicle.yearVehiculo,
        'valorado en': vehicle.valorAuto
          ? formatCurrency({ price: vehicle.valorAuto, fraction: true })
          : undefined,
      })

      if (vehicleInfo.length) comments.push(`Vehículo: ${vehicleInfo}.`)
      insurableAsArgentoModel = vehicle

      break
    }
    case 'Viajes': {
      const travel: SiteApiSchema<'ViajeModel'> = {
        motivo: insurable.data.reason ? TravelReasonEnum[insurable.data.reason].label : undefined,
        fechaSalida: insurable.data.to
          ? DateTime.fromJSDate(insurable.data.to).toISODate()
          : undefined,
        fechaRetorno: insurable.data.from
          ? DateTime.fromJSDate(insurable.data.from).toISODate()
          : undefined,
        destino: insurable.data.destination?.name,
        destinoId: insurable.data.destination?.id,
      }

      const travelInfo = buildComment({
        'se dirige a': travel.destino,
        'por motivo de': travel.motivo,
        'fecha de salida': travel.fechaSalida,
        'fecha de retorno': travel.fechaRetorno,
      })

      if (travelInfo.length) comments.push(`Viaje: ${travelInfo}.`)
      insurableAsArgentoModel = travel

      break
    }
    case 'Mascotas': {
      const pet: SiteApiSchema<'MascotaModel'> = {
        nombre: insurable.data.name,
        tipoMascota: insurable.data.petType
          ? PetTypeEnum[insurable.data.petType]?.label
          : undefined,
        fechaNacimiento: insurable.data.birthDate
          ? DateTime.fromJSDate(insurable.data.birthDate).toISODate()
          : undefined,
        esFertil: insurable.data.fertility === 'Fertile',
        sexo: insurable.data.gender ? PetGenderEnum[insurable.data.gender].label : undefined,
        raza: insurable.data.breed?.name,
        microchip: insurable.data.microchip,
        tamano: insurable.data.size ? PetSizeEnum[insurable.data.size].label : undefined,
      }

      const petInfo = buildComment({
        'se llama': pet.nombre,
        'es un': pet.tipoMascota,
        'que nacio el': pet.fechaNacimiento,
        ['es']: pet.sexo,
        'de raza': pet.raza,
        'con microchip': pet.microchip,
        'su tamaño es': pet.tamano,
        ['esta']: pet.esFertil ? 'fertil' : 'castrado',
      })

      if (petInfo.length) comments.push(`Mascota: ${petInfo}.`)
      insurableAsArgentoModel = pet

      break
    }
  }

  return {
    comments: comments.join(' '),
    insurableAsArgentoModel,
  }
}
